import React, {useEffect, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import {useTranslation} from 'react-i18next';

export type RegionOption = {
    id: number;
    code: string;
    name: { ru?: string; ky?: string; en?: string };
};

type Props = {
    open: boolean;
    amount: number;
    treesCount: number;
    loading?: boolean;
    regions?: RegionOption[];
    showRegionPicker?: boolean;
    onClose: () => void;
    onSubmit: (data: { firstName: string; lastName: string; phone: string; email: string; instagramUrl: string; regionId: number | null }) => void;
};

const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email.trim());
};

const isValidInstagramUsername = (username: string): boolean => {
    const instagramUsernameRegex = /^[a-zA-Z0-9_.]{1,30}$/;
    return instagramUsernameRegex.test(username) && !username.startsWith('.') && !username.endsWith('.');
};

const UserDetailsModal: React.FC<Props> = ({open, amount, treesCount, loading, regions = [], showRegionPicker = true, onClose, onSubmit}) => {
    const {t, i18n} = useTranslation();
    const backdropRef = useRef<HTMLDivElement>(null);

    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [phone, setPhone] = useState('');
    const [email, setEmail] = useState('');
    const [instagramUrl, setInstagramUrl] = useState('');
    const [regionId, setRegionId] = useState<number | null>(null);

    const lang = (i18n.language || 'ru').slice(0, 2) as 'ru' | 'ky' | 'en';
    const regionLabel = (r: RegionOption) => r.name?.[lang] || r.name?.ru || r.name?.en || r.code;

    const phoneDigits = phone.replace(/\D/g, '');
    const phoneOk = phone.startsWith('+') && phoneDigits.length >= 6 && phoneDigits.length <= 15;
    const emailOk = !!email.trim() && isValidEmail(email);
    const formInvalid = !firstName.trim() || !lastName.trim() || !emailOk || !phoneOk;

    // Блокируем скролл фона
    useEffect(() => {
        if (!open) return;
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = prev;
        };
    }, [open]);

    // ESC для закрытия
    useEffect(() => {
        if (!open) return;
        const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open, onClose]);

    const onBackdropClick = (e: React.MouseEvent) => {
        if (e.target === backdropRef.current) onClose();
    };

    const handlePhoneChange = (v: string) => {
        let s = v.replace(/[^\d+]/g, '');
        if (!s.startsWith('+')) s = '+' + s.replace(/\+/g, '');
        s = '+' + s.slice(1).replace(/\+/g, '').replace(/[^\d]/g, '').slice(0, 15);
        setPhone(s);
    };

    const handleInstagramChange = (value: string) => {
        if (!value.trim()) {
            setInstagramUrl('');
            return;
        }

        let cleanValue = value.trim();

        if (cleanValue.includes('instagram.com')) {
            let url = cleanValue;
            if (!url.startsWith('http')) {
                url = 'https://' + url;
            }
            url = url.replace('instagram.com/@', 'instagram.com/');
            const match = url.match(/instagram\.com\/([a-zA-Z0-9_.]+)/);
            if (match && match[1]) {
                const username = match[1];
                if (isValidInstagramUsername(username)) {
                    setInstagramUrl(`https://www.instagram.com/${username}`);
                } else {
                    setInstagramUrl(cleanValue);
                }
            } else {
                setInstagramUrl(cleanValue);
            }
        } else if (cleanValue.startsWith('@')) {
            const username = cleanValue.slice(1);
            if (isValidInstagramUsername(username)) {
                setInstagramUrl(`https://www.instagram.com/${username}`);
            } else {
                setInstagramUrl(cleanValue);
            }
        } else {
            if (isValidInstagramUsername(cleanValue)) {
                setInstagramUrl(`https://www.instagram.com/${cleanValue}`);
            } else {
                setInstagramUrl(cleanValue);
            }
        }
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (formInvalid || loading) return;
        onSubmit({firstName, lastName, phone, email, instagramUrl, regionId});
    };

    if (!open) return null;

    const modal = (
        <div
            ref={backdropRef}
            onClick={onBackdropClick}
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4"
            aria-modal="true"
            role="dialog"
        >
            <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                <div className="bg-white rounded-[20px] shadow-2xl overflow-hidden">
                    {/* Header */}
                    <div className="px-6 pt-6">
                        <h3 className="text-[22px] font-semibold text-accent">
                            {t('donate.modal_title')}
                        </h3>
                        <p className="text-sm text-gray-600 mt-1">
                            {t('donate.modal_subtitle', {count: treesCount})}
                        </p>
                    </div>

                    {/* Form */}
                    <form onSubmit={handleSubmit} noValidate>
                        <div className="px-6 py-5 space-y-3">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                <input
                                    id="udmFirstName"
                                    name="udmFirstName"
                                    className={`rounded-2xl px-4 py-3 w-full bg-[#EEEEEE] text-[15px] ${firstName ? 'text-[#757575]' : 'text-gray-600'}`}
                                    value={firstName}
                                    onChange={(e) => setFirstName(e.target.value)}
                                    placeholder={t('donate.first_name') as string}
                                    autoComplete="given-name"
                                />
                                <input
                                    id="udmLastName"
                                    name="udmLastName"
                                    className={`rounded-2xl px-4 py-3 w-full bg-[#EEEEEE] text-[15px] ${lastName ? 'text-[#757575]' : 'text-gray-600'}`}
                                    value={lastName}
                                    onChange={(e) => setLastName(e.target.value)}
                                    placeholder={t('donate.last_name') as string}
                                    autoComplete="family-name"
                                />
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                <input
                                    id="udmPhone"
                                    name="udmPhone"
                                    className={`rounded-2xl px-4 py-3 w-full bg-[#EEEEEE] text-[15px] ${phone && phone !== '+' ? 'text-[#757575]' : 'text-gray-600'}`}
                                    type="tel"
                                    inputMode="tel"
                                    autoComplete="tel"
                                    maxLength={16}
                                    value={phone}
                                    onChange={(e) => handlePhoneChange(e.target.value)}
                                    onFocus={(e) => {
                                        if (!e.currentTarget.value) setPhone('+');
                                    }}
                                    placeholder={t('donate.phone') as string}
                                />
                                <div className="relative">
                                    <input
                                        id="udmEmail"
                                        name="udmEmail"
                                        className={`rounded-2xl px-4 py-3 w-full bg-[#EEEEEE] text-[15px] ${
                                            email ? 'text-[#757575]' : 'text-gray-600'
                                        } ${
                                            email.trim() && !isValidEmail(email) ? 'border-2 border-red-400' : ''
                                        }`}
                                        type="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        placeholder={t('donate.email') as string}
                                        autoComplete="email"
                                    />
                                    {email.trim() && !isValidEmail(email) && (
                                        <p className="text-red-500 text-xs mt-1 ml-2">
                                            {t('donate.email_invalid') || 'Введите корректный email адрес'}
                                        </p>
                                    )}
                                </div>
                            </div>

                            <div>
                                <input
                                    id="udmInstagram"
                                    name="udmInstagram"
                                    className={`rounded-2xl px-4 py-3 w-full bg-[#EEEEEE] text-[15px] ${instagramUrl ? 'text-[#757575]' : 'text-gray-600'}`}
                                    type="text"
                                    value={instagramUrl}
                                    onChange={(e) => handleInstagramChange(e.target.value)}
                                    placeholder={t('donate.instagram_placeholder') || ''}
                                    autoComplete="off"
                                />
                            </div>

                            {showRegionPicker && regions.length > 0 && (
                                <div>
                                    <select
                                        id="udmRegion"
                                        name="udmRegion"
                                        className={`rounded-2xl px-4 py-3 w-full bg-[#EEEEEE] text-[15px] ${regionId ? 'text-[#757575]' : 'text-gray-600'}`}
                                        value={regionId ?? ''}
                                        onChange={(e) => setRegionId(e.target.value ? Number(e.target.value) : null)}
                                    >
                                        <option value="">{t('donate.region_all_kg')}</option>
                                        {regions.map((r) => (
                                            <option key={r.id} value={r.id}>{regionLabel(r)}</option>
                                        ))}
                                    </select>
                                    <p className="text-xs text-gray-500 mt-1 ml-2">
                                        {t('donate.region_hint')}
                                    </p>
                                </div>
                            )}
                        </div>

                        {/* Footer */}
                        <div className="px-6 pb-6">
                            <div className="flex items-center justify-between gap-3 flex-nowrap">
                                <button
                                    type="button"
                                    onClick={onClose}
                                    className="flex-shrink-0 text-gray-500 hover:text-gray-700 font-medium text-sm transition-colors bg-transparent border-0 cursor-pointer px-2 py-2"
                                >
                                    {t('payment_modal.cancel')}
                                </button>

                                <button
                                    type="submit"
                                    disabled={formInvalid || loading}
                                    className={`flex-1 min-w-0 py-3 px-4 rounded-full font-normal text-white transition inline-flex items-center justify-center gap-2 text-base whitespace-nowrap ${
                                        formInvalid || loading
                                            ? 'bg-gray-300 cursor-not-allowed'
                                            : 'bg-gradient-to-br from-[#93E532] to-accent shadow-[0_8px_16px_rgba(147,229,50,0.35)] hover:opacity-90'
                                    }`}
                                >
                                    {loading ? t('donate.loading') : t('donate.submit', { amount: amount.toLocaleString('ru-RU') })}
                                </button>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );

    return createPortal(modal, document.body);
};

export default UserDetailsModal;
